/* REACT */
import React, { Component, useEffect, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
/* NPM */
import {
  Banner,
  KeyActions,
  KeyActionsProvider,
  Modal,
  toast,
  toastAlert,
  toastUnexpectedError,
} from '@solinkcorp/solink-components';
import { ALL_LOCATIONS, getDayOfWeekNumber, LocaleUtils } from '@solinkcorp/vd-common';
import { StorageAPI } from 'clients';
import Loadable from 'components/Loadable';
import { firebaseAuth } from 'configs/firebase.config';
import { signInWithCustomToken } from 'firebase/auth';
import memoize from 'memoize-one';
import moment from 'moment';
import { connect } from 'react-redux';
import { usePushNotificationLifecycle } from 'src/features/pushNotifications/usePushNotificationLifecycle';
/* STORES */
import { AuthStore } from 'stores/AuthStore';
import CurrentActiveEventStore from 'stores/CurrentActiveEventStore';
import EventFilterStore from 'stores/EventFilterStore';
import { LocationStore } from 'stores/LocationStore';
import PreferenceStore from 'stores/PreferenceStore';
import { useSelector, useStore } from 'stores/store';
import UserStore from 'stores/UserStore';
/* ACTIONS */
import EventActions from 'actions/EventActions';
import { setFilter } from 'actions/filterActions';
/* MODULES */
import AnalyticsLogger from 'models/AnalyticsLogger';
import AuthService from 'models/AuthService';
import { IS_LOWER_ENVIRONMENT, IS_PRODUCTION } from 'src/environment';
import {
  DEFAULT_NOTIFICATIONS_QUERY,
  FAST_NOTIFICATION_DURATION,
  MEDIUM_NOTIFICATION_DURATION,
  MODAL_CONTENT_TYPE,
  NOTIFICATIONS_ANALYTICS_ACTION_TYPE,
  NOTIFICATIONS_CUTOFF,
  NOTIFICATIONS_FILTER_BY_STATE,
  NOTIFICATIONS_FILTER_BY_TYPE,
  NOTIFICATIONS_MIN_QUERY_DATE,
  NOTIFICATIONS_TOTAL_COUNT_DISPLAY_LIMIT,
  TAB_SIDEKICK,
  TAB_VIDEO,
  VIDEO_PAGE_LIVE_FLAG,
} from 'vd-constants';
/* UTILS */
import Browsers from '@solinkcorp/utils/browsers';
import * as EventUtil from 'utils/EventUtil';
import { reportException } from 'utils/ExceptionReportingUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import LocaleUtil from 'utils/LocaleUtil';
import LoggingUtil from 'utils/LoggingUtil';
import TimeFormatter from 'utils/TimeFormatter';
import { getTabFromPathname } from 'utils/urls';
/* COMPONENTS */
import { ComponentLoadingSpinner } from '@solinkcorp/solink-components';
import PushNotifications from 'components/Notifications/PushNotifications';
/* CONTEXTS */
import {
  CommonContext,
  EventsContext,
  SettingsContext,
  UsersContext,
  VideoContext,
} from '../Contexts';
/* HOOKS */
import { useDb } from '@solink/vd-data-layer/index';
import {
  useImpersonator,
  useOnUserSessionChange,
  useOutageStatus,
  useUserSession,
} from '@solinkcorp/authentication';
import { useQueryParams } from '@solinkcorp/utils/hooks';
import { PlayingCamerasProvider } from 'components/PlayingCamerasContext';
import UserFeatureProvider from 'components/UserFeatureProvider';
import PageLoadingSpinner from 'loadingIndicator/PageLoadingSpinner';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import keys from 'lodash/keys';
import map from 'lodash/map';
import merge from 'lodash/merge';
import mergeWith from 'lodash/mergeWith';
import upperFirst from 'lodash/upperFirst';
import AnalyticsLoggerProvider from 'models/AnalyticsLoggerProvider';
import AppBanners from 'src/components/AppBanners/AppBanners';
import ReloadNotice from 'src/components/ReloadNotice/ReloadNotice';
import { isKiosk } from 'src/core/utils/kioskUtils';
import useLocale from 'src/hooks/useLocale';
import { EventStandardProvider } from 'utils/EventStandardCache/eventStandardCache';
import { getQueryProps } from 'utils/RouteUtil';
import TopLevelRoutes from '../TopLevelRoutes';
import styles from './AuthenticatedApp.module.css';
import { onUserSessionChange } from './loginAdapter';

const SingleEventPlayer = Loadable({
  loader: () => import('components/EventPlayer/SingleEventPlayer'),
  loading: ComponentLoadingSpinner,
});

/**
 * Re-reads `FeaturesUtil.isLiveViewOnly()` whenever user features change so shortcut disabling stays in sync.
 */
function LiveViewKeyActionsProvider({ children }) {
  const [, rerender] = useReducer((c) => c + 1, 0);

  useEffect(() => {
    const listener = () => rerender();
    FeaturesUtil.subscribe(listener, false);
    return () => FeaturesUtil.unsubscribe(listener);
  }, []);

  return (
    <KeyActionsProvider shortcutsDisabled={FeaturesUtil.isLiveViewOnly()}>
      {children}
    </KeyActionsProvider>
  );
}

/**
 * State properties that should be replaced instead of merged
 * when setting a new state, specified as `${prop}.${key}`
 */
const REPLACEABLE_STATE_PROPS = [
  'events.filters.selectedFilter',
  'events.filters.types',
  'events.filters.searchCapsules',
  'locations.data',
  'filters.location',
  'locations.restrictions',
  'users.current',
  'video.timelineEvents',
  'events.filters.subTypes',
];

function withStore(Component) {
  return function WithStore(props) {
    const store = useStore();
    return <Component {...props} store={store} />;
  };
}

/**
 * Wraps up `React.createContext` to assign a displayName to provided and consumer components,
 * allowing to identify each context in react dev tools.
 * The components' display name will be a CamelCased version of the provided name.
 * @param {string} name kebab-cased or camelCased react context name
 */
function _createReactContext(name) {
  const context = React.createContext({});
  const capName = name.split('-').map(upperFirst).join('');
  context.displayName = capName;
  return context;
}

export const NotificationsContext = _createReactContext('notifications-context');
export const ReportsContext = _createReactContext('reports-context');
export const FiltersContext = _createReactContext('filters-context');
export const ContextContext = _createReactContext('context-context');
export { CommonContext, EventsContext, SettingsContext, UsersContext, VideoContext };
class AuthenticatedApp extends Component {
  constructor(props) {
    super(props);
    const { user, impersonator, query } = props;
    const { innerWidth, innerHeight } = window;
    const contextineCreds = AuthStore.getContextine();
    const { annotationTypes, useLocalIpAsPrimary } = AuthStore.getCustomer();
    const { localeCode } = props.localeState;
    const locale = LocaleUtil.getLocale(localeCode);
    const isImpersonating = !!impersonator;
    const {
      capsules: searchCapsules,
      searchString,
      searchOperator,
      searchStringRegex,
      showRevenue,
      eventFilterState,
    } = EventFilterStore.getAppState();

    this.masterLocationsList = LocationStore.getLocations();
    this.masterRealLocationsList = LocationStore.getLocations(true);

    this.camerasDictionary = LocationStore.getAllLocationCamerasDict();

    const storedVideoPageLiveFlag = StorageAPI.getLocally(VIDEO_PAGE_LIVE_FLAG);
    const rawIsLiveToggleOn =
      FeaturesUtil.isLiveViewOnly() ||
      query.get('mode') === 'live' ||
      (!FeaturesUtil.hasNoFullAccessInKiosk() &&
        (storedVideoPageLiveFlag == null ? true : storedVideoPageLiveFlag));

    moment.locale(localeCode.toLowerCase());

    this.notificationsQuery = cloneDeep(DEFAULT_NOTIFICATIONS_QUERY);

    this.getShortcuts = memoize((isGuest) => {
      return [
        {
          shortcut: 'alt+mod+shift+n',
          action: this.toggleNotifications,
          info: {
            title: locale.notifications.viewNotifications,
            group: locale.general.general,
          },
          visible: !isGuest,
          isFormSafe: true,
        },
      ];
    });

    this.state = {
      authData: {
        user: user.jwtUser,
        tenantId: user.customerId,
        contextineCreds,
        contextineEnabled: !!contextineCreds,
        isImpersonating,
        loggedIn: false,
        isTestUser: AuthStore.isTestUser(),
        annotationTypes,
        useLocalIpAsPrimary,
        expiration: user.jwtUser.exp * 1000,
      },
      appState: {
        showTabs: true,
        reloadNotice: null,
        reloadNoticeCountdownMs: null,
        reloadNoticeDeadline: null,
        setReloadNoticeDeadline: this.setReloadNoticeDeadline,
        bannerHeightPx: 0,
        setReloadNotice: this.setReloadNotice,
        setBannerHeightPx: this.setBannerHeightPx,
        showIntegrationBanner: IS_LOWER_ENVIRONMENT,
        setIsIntegrationBannerVisible: this.setIsIntegrationBannerVisible,
      },
      events: {
        updateContextMenuStatus: this.updateContextMenuStatus,
        updateHistogramOpenState: this.updateHistogramOpenState,
        isEventContextMenuOpen: false,
        pinnedEvent: null,
        videoStartTime: null,
        reports: [],
        histogramOpen: true,
        filters: {
          changeDate: null,
          searchCapsules,
          searchOperator,
          searchString,
          searchStringRegex,
          selectedFilter: null,
          selectedFilterId: null,
          showRevenue,
          types: eventFilterState,
        },
      },
      filters: {
        eventFilters: eventFilterState,
      },
      locale,
      locations: {
        loading: true,
        restrictions: {},
      },
      notifications: {
        changeTime: new Date().getTime(),
        confirmationActionText: null,
        confirmationMessage: null,
        confirmationTitle: null,
        filterBy: NOTIFICATIONS_FILTER_BY_STATE.all,
        filterByOptions: this.getFilterByOptions(locale),
        inboxCount: 0,
        loading: false,
        showAllNotifications: false,
        showUpdateConfirmation: false,
        updateAction: null,
        activeSingleEventPlayerNotificationId: null,
        activeSingleEventPlayerNotificationEventId: null,
        activeSingleEventPlayerNotificationEventTitle: null,
        notificationErrorIndicies: {},
      },
      reports: {
        data: [],
        updateTime: new Date().getTime(),
        loading: true,
      },
      settings: {
        isDigestEnabled: UserStore.getIsDigestEnabled(),
        resetReportSettings: () => this.resetReportSettings(),
        setReportSettings: (settings) => this.setReportSettings(settings),
        reports: {
          showThresholdAlert: false,
          thresholdTemplateAverage: null,
          thresholdTemplateId: null,
          thresholdType: null,
        },
        preferences: {
          shouldAllowSupportAccess: UserStore.getSupportAccessState(),
          supportAccessExpiresAt: UserStore.getSupportAccessExpiresAt(),
          shouldShowExperimentalFeatures: PreferenceStore.shouldShowExperimentalFeatures(),
          shouldShowAllEventThumbnails: PreferenceStore.shouldShowAllEventThumbnails(),
        },
      },
      shared: {
        isContextMenuOpen: false,
      },
      users: {
        current: UserStore.getCurrentUser() || {},
        data: [],
        digestSettings: null,
        hasSMSNumber: false,
        isLoaded: false,
        loading: true,
        locationProcessing: false,
        smsId: null,
        smsNumber: null,
        impersonating: AuthStore.isImpersonating(),
      },
      video: {
        updateTimelineEvents: this.updateTimelineEvents,
        syncTimelineEvent: this.syncTimelineEvent,
        updateLiveToggle: this.updateLiveToggle,
        rawIsLiveToggleOn,
        timelineEvents: [],
      },
      window: {
        height: innerHeight,
        width: innerWidth,
      },
    };
  }

  static defaultProps = {
    notifications: [],
  };

  componentDidUpdate(prevProps, prevState) {
    const { notifications, localeState, guestTokenExpiryDate } = this.props;
    const {
      notifications: { inboxCount: oldInboxCount },
    } = this.state;

    const inboxCount = this.getInboxCount({ notifications });
    if (inboxCount !== oldInboxCount) {
      this.cleanAndSetState({ notifications: { inboxCount } });
    }

    // Only log the MegaState if we are not in production domain or if we have the localStorage.debug set.
    if (LoggingUtil.shouldLog()) {
      console.group('APP STATE UPDATES');
      console.info('%cPREVIOUS', 'font-weight: 900; color: #005f98', prevState);
      console.info('%cNEXT    ', 'font-weight: 900; color: #009f58', this.state);
      console.groupEnd();
    }

    // Once guest is authenticated, show the banner and hide the banner after some time.
    if (this.state.users.current?.id !== prevState.users.current?.id) {
      if (guestTokenExpiryDate) {
        this.setState({ showGuestAccessBanner: true });
        setTimeout(() => this.setState({ showGuestAccessBanner: false }), 8000);
      }
    }

    if (localeState !== prevProps.localeState) {
      this._handleLocaleStoreChange();
    }
  }

  componentDidMount() {
    const {
      video: { rawIsLiveToggleOn },
    } = this.state;
    const { location } = this.props;
    const staticLoading = document.getElementById('static-loading');
    if (staticLoading) {
      staticLoading.classList.add('hide');
    }

    // EVENT LISTENERS //
    window.addEventListener('error', ({ error }) =>
      reportException({
        error,
        showToast: IS_PRODUCTION,
      }),
    );

    // ADD STORE CHANGE LISTENERS /////////////////////////////////////////////
    AuthStore.addChangeListener(this._handleAuthStoreChange);
    CurrentActiveEventStore.addChangeListener(this._handleCurrentActiveEventStoreChange);
    EventFilterStore.addChangeListener(this._handleEventFilterStoreChange);
    LocationStore.addChangeListener(this._handleLocationStoreChange);
    PreferenceStore.addChangeListener(this._handlePreferenceStoreChange);
    UserStore.addChangeListener(this._handleUserStoreChange);

    // Check if we're launching video from URL directly
    let launchVideoLiveFromURL;
    const { cameraIds, date } = getQueryProps({ location });

    if (!isEmpty(cameraIds)) {
      launchVideoLiveFromURL = !date;
    }

    if (
      !FeaturesUtil.isLiveViewOnly() &&
      launchVideoLiveFromURL != null &&
      launchVideoLiveFromURL !== rawIsLiveToggleOn
    ) {
      this.cleanAndSetState({ video: { rawIsLiveToggleOn: launchVideoLiveFromURL } });
    }
  }

  componentWillUnmount() {
    window.onblur = null;

    // REMOVE STORE CHANGE LISTENERS //////////////////////////////////////////
    AuthStore.removeChangeListener(this._handleAuthStoreChange);
    CurrentActiveEventStore.removeChangeListener(this._handleCurrentActiveEventStoreChange);
    EventFilterStore.removeChangeListener(this._handleEventFilterStoreChange);
    LocationStore.removeChangeListener(this._handleLocationStoreChange);
    PreferenceStore.removeChangeListener(this._handlePreferenceStoreChange);
    UserStore.removeChangeListener(this._handleUserStoreChange);
  }

  getCommonContext = memoize(
    (
      locale,
      locations,
      authData,
      shared,
      masterLocationsList,
      masterRealLocationsList,
      masterPhysicalLocationsList,
      camerasDictionary,
      locationsById,
      appState,
    ) => {
      return {
        locale,
        locations,
        authData,
        shared,
        updateContextMenuStatus: this.updateContextMenuStatus,
        masterLocationsList,
        masterRealLocationsList,
        masterPhysicalLocationsList,
        camerasDictionary,
        locationsById,
        appState,
      };
    },
  );

  getVideoContext = memoize((videoState, pathname) => {
    const tabName = getTabFromPathname(pathname);
    const isLiveToggleOn =
      tabName === TAB_VIDEO || tabName === TAB_SIDEKICK ? videoState.rawIsLiveToggleOn : false;
    return {
      ...videoState,
      isLiveToggleOn,
    };
  });

  render() {
    const {
      events,
      settings,
      notifications,
      locations,
      locale,
      appState,
      users,
      reports,
      filters,
      authData,
      shared,
    } = this.state;

    const {
      router: {
        location: { pathname },
      },
    } = this.props;

    const commonContext = this.getCommonContext(
      locale,
      locations,
      authData,
      shared,
      this.masterLocationsList,
      this.masterRealLocationsList,
      this.masterPhysicalLocationsList,
      this.camerasDictionary,
      this.locationsById,
      appState,
    );

    const video = this.getVideoContext(this.state.video, pathname);

    return (
      <LiveViewKeyActionsProvider>
        <FcmBridge user={this.props.user} onNotification={this.handleForegroundNotification} />
        <EventsContext.Provider value={events}>
          <VideoContext.Provider value={video}>
            <SettingsContext.Provider value={settings}>
              <NotificationsContext.Provider value={notifications}>
                <CommonContext.Provider value={commonContext}>
                  <UsersContext.Provider value={users}>
                    <ReportsContext.Provider value={reports}>
                      <FiltersContext.Provider value={filters}>
                        <EventStandardProvider customerId={authData.tenantId}>
                          <UserFeatureProvider>
                            <AnalyticsLoggerProvider>
                              <PlayingCamerasProvider>
                                {this._renderContent()}
                              </PlayingCamerasProvider>
                            </AnalyticsLoggerProvider>
                          </UserFeatureProvider>
                        </EventStandardProvider>
                      </FiltersContext.Provider>
                    </ReportsContext.Provider>
                  </UsersContext.Provider>
                </CommonContext.Provider>
              </NotificationsContext.Provider>
            </SettingsContext.Provider>
          </VideoContext.Provider>
        </EventsContext.Provider>
      </LiveViewKeyActionsProvider>
    );
  }

  _renderContent = () => {
    const { guestTokenExpiryDate, notifications, notificationsPreCutoff, outageStatus } =
      this.props;

    const {
      locale,
      notifications: {
        changeTime,
        confirmationActionText,
        confirmationMessage,
        confirmationTitle,
        filterBy,
        filterByOptions,
        inboxCount,
        loading: notificationsLoading,
        showAllNotifications,
        showUpdateConfirmation,
        updateAction,
        activeSingleEventPlayerNotificationEventId,
        activeSingleEventPlayerNotificationEventTitle,
        notificationErrorIndicies,
      },
      showGuestAccessBanner,
    } = this.state;
    const { userId } = AuthService.session;

    const notificationsList =
      notificationsPreCutoff && this.notificationsQueryPreCutoff
        ? [...notifications, ...notificationsPreCutoff]
        : notifications;

    return (
      <KeyActions actions={this.getShortcuts(AuthService.isGuest)}>
        <div className={styles.authenticatedApp}>
          <AppBanners outageStatus={outageStatus} />
          <ReloadNotice />
          <Modal
            actions={[
              {
                isPrimary: false,
                onClick: this.closeAllNotificationsConfirmation,
                text: locale.general.cancel,
              },
              {
                onClick: updateAction,
                text: confirmationActionText,
              },
            ]}
            focusOnPrimary={true}
            isLoading={notificationsLoading}
            isOpen={showUpdateConfirmation}
            message={confirmationMessage}
            onClose={this.closeAllNotificationsConfirmation}
            onConfirm={updateAction}
            title={confirmationTitle}
          />
          {/* Modal for Notification single event player */}
          <Modal
            bodyStyle={{ padding: 0, overflow: 'hidden', fontSize: '12px' }}
            isOpen={activeSingleEventPlayerNotificationEventId}
            title={activeSingleEventPlayerNotificationEventTitle}
            className="event-player-modal"
            dataTestId="event-player-modal"
            colorMode="dark"
            type="full-screen"
            onClose={this.closeNotificationSingleEventPlayer}
            contentType={MODAL_CONTENT_TYPE.EVENT_PLAYER}
          >
            <SingleEventPlayer
              eventId={activeSingleEventPlayerNotificationEventId}
              dismissAction={this.closeNotificationSingleEventPlayer}
              onError={this.updateNotificationError}
              noModal
            />
          </Modal>
          <PushNotifications
            archiveAll={this.confirmBulkArchiveNotifications}
            changeTime={changeTime}
            count={inboxCount}
            filterBy={filterBy}
            filterByOptions={filterByOptions}
            markAllAsRead={this.confirmBulkMarkAsReadNotifications}
            notifications={notificationsList}
            onArchive={this.archiveNotification}
            onClose={this.onCloseNotifications}
            onFilter={this.filterNotifications}
            onMarkAsRead={this.markNotificationAsRead}
            onView={this.viewNotification}
            onError={this.onNotificationError}
            readCutoffDate={this.notifReadCutoff}
            showAll={showAllNotifications}
            totalCount={notificationsList.length}
            userId={userId}
            notificationErrorIndicies={notificationErrorIndicies}
          />
          <TopLevelRoutes
            updateLiveToggle={this.updateLiveToggle}
            onShowAllNotifications={this.onShowAllNotifications}
            inboxCount={inboxCount}
          />
          <Banner
            className="guest-access-expiry-banner"
            show={showGuestAccessBanner}
            text={`${locale.guestAccess.guestAccessExpiresOn} ${moment(guestTokenExpiryDate).format(
              'LLL',
            )}`}
          />
        </div>
      </KeyActions>
    );
  };

  // COMPONENT HELPERS ////////////////////////////////////////////////////////
  handleForegroundNotification = (latestNotification) => {
    const {
      locale,
      notifications: { showAllNotifications },
    } = this.state;
    const { body, eventId, id, launchURL, subtitle, title, type } = latestNotification;

    // Only show popups if the notifications panel isn't open
    if (!showAllNotifications) {
      const timeFormat = TimeFormatter.getString({ seconds: true });
      const timeString = moment().format(timeFormat);

      toastAlert({
        autoClose: 10000,
        message: body,
        notificationType: locale.notifications.nounSingular, // type
        title,
        subtitle,
        templateProps: {
          defaultAlertType: locale.general.info,
          dismissAction: () => {}, // mark notification as read when FCM messages have notification id
          dismissText: locale.general.dismiss,
          viewAction: () =>
            this.viewNotification({
              eventId,
              id,
              launchURL,
              subtitle,
              title,
              type,
            }),
          viewText: locale.general.view,
        },
        timeString,
        severity: 'info',
      });
    }
  };

  getNotificationSeverity = () => {
    return '';
  };

  onShowAllNotifications = () => {
    // Open the panel, but in loading mode
    const { userId } = AuthService.session;
    this.cleanAndSetState({ notifications: { showAllNotifications: true } });
    AnalyticsLogger.notificationDrawerOpened({ userId });
  };

  updateActiveNotificationData = ({ id, eventId, title }) => {
    this.cleanAndSetState({
      notifications: {
        activeSingleEventPlayerNotificationEventId: eventId,
        activeSingleEventPlayerNotificationEventTitle: title,
        activeSingleEventPlayerNotificationId: id,
      },
    });

    this.updateNotificationError();
  };

  closeNotificationSingleEventPlayer = () => {
    EventActions.closeEventPlayer();

    this.cleanAndSetState({
      notifications: {
        activeSingleEventPlayerNotificationEventId: null,
        activeSingleEventPlayerNotificationEventTitle: null,
        activeSingleEventPlayerNotificationId: null,
      },
    });
  };

  updateNotificationError = () => {
    const { activeSingleEventPlayerNotificationId, notificationErrorIndicies } =
      this.state.notifications;

    if (!activeSingleEventPlayerNotificationId) return;

    const newNotificationErrorIndicies = cloneDeep(notificationErrorIndicies);
    newNotificationErrorIndicies[activeSingleEventPlayerNotificationId] = true;

    this.cleanAndSetState({
      notifications: { notificationErrorIndicies: newNotificationErrorIndicies },
    });
  };

  setIsIntegrationBannerVisible = (isVisible) => {
    this.cleanAndSetState({
      appState: {
        showIntegrationBanner: isVisible,
      },
    });
  };

  setReloadNotice = (reloadNotice, countdownMs = null) => {
    this.cleanAndSetState({
      appState: {
        reloadNotice,
        reloadNoticeCountdownMs: countdownMs,
      },
    });
  };
  setReloadNoticeDeadline = (reloadNoticeDeadline) => {
    this.cleanAndSetState({
      appState: {
        reloadNoticeDeadline,
      },
    });
  };
  setBannerHeightPx = (height = 0) => {
    this.cleanAndSetState({
      appState: {
        bannerHeightPx: height,
      },
    });
  };

  closeAllNotificationsConfirmation = ({ inboxCount = this.state.notifications.inboxCount }) =>
    this.cleanAndSetState({
      notifications: {
        confirmationActionText: null,
        confirmationMessage: null,
        confirmationTitle: null,
        inboxCount,
        loading: false,
        updateAction: null,
        showUpdateConfirmation: false,
      },
    });

  getNotificationsTotalCount = () =>
    this.props.notifications.length + get(this.props.notificationsPreCutoff, 'length', 0);

  updateAllNotifications =
    ({
      cutoffCallback = () => {},
      cutoffPropName,
      getCutoffValue = () => null,
      updateMatches,
      updateValue,
    }) =>
    (selectedIds) => {
      const { notificationsDict, notificationsPreCutoffDict } = this.props;
      const { firestore } = this.props.store;
      const {
        authData: {
          user: { sub: doc },
        },
        notifications: { inboxCount: count },
      } = this.state;
      const { userId } = AuthService.session;
      const isEvent = selectedIds.nativeEvent && selectedIds.nativeEvent instanceof Event;
      const isBulkUpdate = !isEmpty(selectedIds) && !isEvent;
      const isArchive = cutoffPropName === NOTIFICATIONS_CUTOFF.archive;
      const params = { collection: 'users', doc };
      const cutoffValue = getCutoffValue();
      const promises = [];
      let inboxCount = count;

      // Update bulk selection
      if (isBulkUpdate) {
        forEach(selectedIds, (value, id) => {
          const data = notificationsDict?.[id] || notificationsPreCutoffDict?.[id];
          if (!data || !updateMatches(data)) return;
          if (this.isNotificationUnread(data)) {
            inboxCount--;
          }
          const config = {
            ...params,
            subcollections: [{ collection: 'notifications', doc: id }],
          };
          // This isn't batched, because we'd have to fetch each ref first, so we'd end up with more
          // requests than just updating each notification this way.
          promises.push(firestore.update(config, updateValue));
        });
      } else {
        inboxCount = 0;
        // Set loading state while we perform this op
        this.cleanAndSetState({ notifications: { loading: true } });
        promises.push(this.updateNotifCutoff({ propName: cutoffPropName, value: cutoffValue }));
      }

      const postProcessing = () => {
        if (!isBulkUpdate) {
          cutoffCallback({ [cutoffPropName]: cutoffValue });
        }
        if (!isEmpty(promises)) {
          const analyticsProperties = {
            userId,
            actionType: isBulkUpdate
              ? NOTIFICATIONS_ANALYTICS_ACTION_TYPE.bulk
              : NOTIFICATIONS_ANALYTICS_ACTION_TYPE.all,
            notificationIds: isBulkUpdate ? keys(selectedIds) : [],
            notificationCount: isBulkUpdate
              ? keys(selectedIds).length
              : this.getNotificationsTotalCount(),
          };

          if (isArchive) {
            AnalyticsLogger.notificationArchived(analyticsProperties);
          } else {
            AnalyticsLogger.notificationMarkedRead(analyticsProperties);
          }
        }
      };

      // If no notifications matched, then just remove the loading state and resolve
      if (isEmpty(promises)) {
        this.closeAllNotificationsConfirmation({ inboxCount });
        return Promise.resolve().then(postProcessing);
      }
      // Otherwise, only remove the loading state once all updates have completed
      return Promise.all(promises)
        .then(() => this.closeAllNotificationsConfirmation({ inboxCount }))
        .then(postProcessing);
    };

  updateNotifCutoff = async ({ propName, value }) => {
    const { firestore } = this.props.store;
    const doc = this.props.user.userId;
    const snap = await firestore.get({ collection: 'users', doc });
    const { metadata: source } = snap.data() ?? {};
    const metadata = { ...source, [propName]: value };
    if (propName === NOTIFICATIONS_CUTOFF.archive) {
      // Also update read cutoff date, to avoid potential errors
      metadata[NOTIFICATIONS_CUTOFF.read] = value;
    }
    snap.ref.set({ metadata }, { merge: true });
    return value;
  };

  filterNotifications = ({ filterBy, ...cutoffs }) => {
    const { firestore } = this.props.store;
    const doc = this.props.user.userId;
    const {
      notifications: { filterBy: currentFilterBy },
    } = this.state;

    if (cutoffs[NOTIFICATIONS_CUTOFF.archive] || cutoffs[NOTIFICATIONS_CUTOFF.read]) {
      this.notifArchiveCutoff = cutoffs[NOTIFICATIONS_CUTOFF.archive] || this.notifArchiveCutoff;
      this.notifReadCutoff = cutoffs[NOTIFICATIONS_CUTOFF.read] || this.notifReadCutoff;
    } else if (filterBy === currentFilterBy) {
      return;
    }

    const promises = [];
    const filterSource = filterBy || currentFilterBy;
    const preCutoffKey = 'notificationsPreCutoff';

    // Unset the existing listener
    firestore.unsetListener(this.notificationsQuery);
    if (this.notificationsQueryPreCutoff) {
      firestore.unsetListener(this.notificationsQueryPreCutoff);
    }

    // Prime/reset the new query
    this.notificationsQuery = cloneDeep(DEFAULT_NOTIFICATIONS_QUERY);
    this.notificationsQuery.doc = doc;

    // Prepare the new query (or queries)
    switch (filterSource) {
      case NOTIFICATIONS_FILTER_BY_STATE.all:
      default:
        this.notificationsQuery.where = [
          ['created', '>', this.notifArchiveCutoff],
          [
            'state',
            'in',
            [NOTIFICATIONS_FILTER_BY_STATE.read, NOTIFICATIONS_FILTER_BY_STATE.unread],
          ],
        ];
        // This filter only requires one query
        this.notificationsQueryPreCutoff = null;
        break;

      case NOTIFICATIONS_FILTER_BY_STATE.archived:
        this.notificationsQuery.where = [
          ['created', '>', this.notifArchiveCutoff],
          ['state', '==', NOTIFICATIONS_FILTER_BY_STATE.archived],
        ];
        // This filter requires 2 queries, so set a "pre-cutoff" query as well
        this.notificationsQueryPreCutoff = cloneDeep(DEFAULT_NOTIFICATIONS_QUERY);
        this.notificationsQueryPreCutoff.storeAs = preCutoffKey;
        this.notificationsQueryPreCutoff.doc = doc;
        this.notificationsQueryPreCutoff.where = [
          ['created', '<', this.notifArchiveCutoff],
          [
            'state',
            'in',
            [
              NOTIFICATIONS_FILTER_BY_STATE.archived,
              NOTIFICATIONS_FILTER_BY_STATE.read,
              NOTIFICATIONS_FILTER_BY_STATE.unread,
            ],
          ],
        ];
        break;

      case NOTIFICATIONS_FILTER_BY_STATE.read:
        this.notificationsQuery.where = [
          ['created', '>', this.notifReadCutoff || this.notifArchiveCutoff],
          ['state', '==', NOTIFICATIONS_FILTER_BY_STATE.read],
        ];
        if (this.notifReadCutoff) {
          // This filter requires 2 queries, so set a "pre-cutoff" query as well
          this.notificationsQueryPreCutoff = cloneDeep(DEFAULT_NOTIFICATIONS_QUERY);
          this.notificationsQueryPreCutoff.storeAs = preCutoffKey;
          this.notificationsQueryPreCutoff.doc = doc;
          this.notificationsQueryPreCutoff.where = [
            ['created', '<', this.notifReadCutoff],
            ['created', '>', this.notifArchiveCutoff],
            [
              'state',
              'in',
              [NOTIFICATIONS_FILTER_BY_STATE.read, NOTIFICATIONS_FILTER_BY_STATE.unread],
            ],
          ];
        } else {
          // This filter only requires one query
          this.notificationsQueryPreCutoff = null;
        }
        break;

      case NOTIFICATIONS_FILTER_BY_STATE.unread:
        this.notificationsQuery.where = [
          ['created', '>', this.notifReadCutoff || this.notifArchiveCutoff],
          ['state', '==', NOTIFICATIONS_FILTER_BY_STATE.unread],
        ];
        // This filter only requires one query
        this.notificationsQueryPreCutoff = null;
        break;

      case NOTIFICATIONS_FILTER_BY_TYPE.alarm:
        this.notificationsQuery.where = [
          ['created', '>', this.notifArchiveCutoff],
          [
            'state',
            'in',
            [NOTIFICATIONS_FILTER_BY_STATE.read, NOTIFICATIONS_FILTER_BY_STATE.unread],
          ],
          ['type', '==', NOTIFICATIONS_FILTER_BY_TYPE.alarm],
        ];
        // This filter only requires one query
        this.notificationsQueryPreCutoff = null;
        break;

      case NOTIFICATIONS_FILTER_BY_TYPE.clip:
        this.notificationsQuery.where = [
          ['created', '>', this.notifArchiveCutoff],
          [
            'state',
            'in',
            [NOTIFICATIONS_FILTER_BY_STATE.read, NOTIFICATIONS_FILTER_BY_STATE.unread],
          ],
          ['type', '==', NOTIFICATIONS_FILTER_BY_TYPE.clip],
        ];
        // This filter only requires one query
        this.notificationsQueryPreCutoff = null;
        break;

      case NOTIFICATIONS_FILTER_BY_TYPE.event:
        this.notificationsQuery.where = [
          ['created', '>', this.notifArchiveCutoff],
          [
            'state',
            'in',
            [NOTIFICATIONS_FILTER_BY_STATE.read, NOTIFICATIONS_FILTER_BY_STATE.unread],
          ],
          ['type', '==', NOTIFICATIONS_FILTER_BY_TYPE.event],
        ];
        // This filter only requires one query
        this.notificationsQueryPreCutoff = null;
        break;
    }

    // Save the new filter value
    this.cleanAndSetState({ notifications: { filterBy: filterSource, loading: true } });

    // Get the current content, and set a new listener
    promises.push(
      firestore.get(this.notificationsQuery).then(({ docs }) => {
        this.cleanAndSetState({
          notifications: { inboxCount: this.getInboxCount({ docs, filterBy: filterSource }) },
        });
        firestore.setListener(this.notificationsQuery);
      }),
    );

    if (this.notificationsQueryPreCutoff) {
      promises.push(
        firestore.get(this.notificationsQueryPreCutoff).then(() => {
          firestore.setListener(this.notificationsQueryPreCutoff);
        }),
      );
    }

    Promise.all(promises).then(() =>
      this.cleanAndSetState({
        notifications: { changeTime: new Date().getTime(), loading: false },
      }),
    );
  };

  archiveAllNotifications = this.updateAllNotifications({
    cutoffCallback: this.filterNotifications,
    cutoffPropName: NOTIFICATIONS_CUTOFF.archive,
    getCutoffValue: () => new Date().getTime(),
    updateMatches: ({ state }) => state !== NOTIFICATIONS_FILTER_BY_STATE.archived,
    updateValue: { state: NOTIFICATIONS_FILTER_BY_STATE.archived },
  });

  markAllAsRead = this.updateAllNotifications({
    cutoffCallback: this.filterNotifications,
    cutoffPropName: NOTIFICATIONS_CUTOFF.read,
    getCutoffValue: () => new Date().getTime(),
    updateMatches: ({ state }) => state !== NOTIFICATIONS_FILTER_BY_STATE.read,
    updateValue: { state: NOTIFICATIONS_FILTER_BY_STATE.read },
  });

  confirmBulkNotificationAction =
    ({ allText, operation, selectedText, updateAction }) =>
    async (selectedIds) => {
      const { locale } = this.state;
      const hasSelection = !isEmpty(selectedIds);

      if (hasSelection) {
        updateAction(selectedIds);
      } else {
        const total = this.getNotificationsTotalCount();
        const actionText = hasSelection ? get(locale, selectedText, '') : get(locale, allText, '');
        this.cleanAndSetState({
          notifications: {
            confirmationActionText: actionText,
            confirmationMessage: LocaleUtil.getLocalizedStr({
              string: locale.notifications.confirmationMessage,
              replacements: {
                noun: total ? locale.notifications.nounPlural : locale.notifications.nounSingular,
                operation: get(locale, operation).toLowerCase(),
                total: total > NOTIFICATIONS_TOTAL_COUNT_DISPLAY_LIMIT ? `${total}+` : total,
              },
            }),
            confirmationTitle: actionText,
            updateAction,
            showUpdateConfirmation: true,
          },
        });
      }
    };

  confirmBulkArchiveNotifications = this.confirmBulkNotificationAction({
    allText: 'notifications.archiveAll',
    operation: 'notifications.archive',
    selectedText: 'notifications.archive',
    updateAction: this.archiveAllNotifications,
  });

  confirmBulkMarkAsReadNotifications = this.confirmBulkNotificationAction({
    allText: 'notifications.markAllAsRead',
    operation: 'notifications.update',
    selectedText: 'notifications.markAsRead',
    updateAction: this.markAllAsRead,
  });

  onCloseNotifications = () =>
    this.cleanAndSetState({
      notifications: { showAllNotifications: false },
    });

  archiveNotification = async ({ id } = {}) => {
    if (!id) {
      console.warn('Cannot dismiss notification: invalid data');
      return;
    }

    // Update the notification in the DB
    const { firestore } = this.props.store;
    const { notificationsDict, notificationsPreCutoffDict } = this.props;
    const doc = this.props.user.userId;
    const { userId } = AuthService.session;
    const data = notificationsDict?.[id] || notificationsPreCutoffDict?.[id];
    if (!data) {
      console.warn('Cannot dismiss notification: notification data not loaded yet', id);
      return;
    }
    const { state } = data;
    const isUnread = state === NOTIFICATIONS_FILTER_BY_STATE.unread;

    await firestore.update(
      {
        collection: 'users',
        doc,
        subcollections: [{ collection: 'notifications', doc: id }],
      },
      { state: NOTIFICATIONS_FILTER_BY_STATE.archived },
    );
    this.cleanAndSetState({
      notifications: {
        changeTime: new Date().getTime(),
        inboxCount: this.state.notifications.inboxCount - (isUnread ? 1 : 0),
      },
    });
    AnalyticsLogger.notificationArchived({
      actionType: NOTIFICATIONS_ANALYTICS_ACTION_TYPE.single,
      notificationCount: 1,
      notificationIds: [id],
      userId,
    });
  };

  markNotificationAsRead = async ({ id }) => {
    // Update the notification in the DB
    const { firestore } = this.props.store;
    const doc = this.props.user.userId;
    const { userId } = AuthService.session;
    await firestore.update(
      {
        collection: 'users',
        doc,
        subcollections: [{ collection: 'notifications', doc: id }],
      },
      { state: NOTIFICATIONS_FILTER_BY_STATE.read },
    );
    this.cleanAndSetState({
      notifications: {
        changeTime: new Date().getTime(),
        inboxCount: this.state.notifications.inboxCount - 1,
      },
    });
    AnalyticsLogger.notificationMarkedRead({
      actionType: NOTIFICATIONS_ANALYTICS_ACTION_TYPE.single,
      notificationCount: 1,
      notificationIds: [id],
      userId,
    });
  };

  markNotificationAsError = async ({ id }) => {
    // Update the notification in the DB
    const { firestore } = this.props.store;
    const userId = this.props.user.userId;
    await firestore.update(
      {
        collection: 'users',
        doc: userId,
        subcollections: [{ collection: 'notifications', doc: id }],
      },
      { state: NOTIFICATIONS_FILTER_BY_STATE.error },
    );
    this.cleanAndSetState({
      notifications: {
        changeTime: new Date().getTime(),
        inboxCount: this.state.notifications.inboxCount - 1,
      },
    });
    AnalyticsLogger.notificationMarkedError({
      actionType: NOTIFICATIONS_ANALYTICS_ACTION_TYPE.single,
      notificationCount: 1,
      notificationIds: [id],
      userId,
    });
  };

  onNotificationError = ({ id }) => {
    const isImpersonating = !!this.props.impersonator;

    if (id && !isImpersonating) {
      this.markNotificationAsError({ id, shouldSendAnalytics: true });
    }
  };

  viewNotification = (
    { eventId, id, launchURL, subtitle, title, type },
    openInNewWindow = false,
  ) => {
    const isImpersonating = !!this.props.impersonator;
    const { notificationsDict } = this.props;
    const { userId } = AuthService.session;

    // get eventId from notificationsDict since eventId does not exist on firebase cloud message
    if (!eventId) {
      eventId = notificationsDict?.[id]?.eventId;
    }

    if (!eventId && !launchURL) {
      console.warn('Cannot view notification: invalid data');
      return;
    }

    if (openInNewWindow) {
      const url = window.location.protocol + '//' + window.location.host + '/events/' + eventId;
      window.open(url);
    }

    // openInNewWindow === true when viewing notifications at /video/ route
    if (!openInNewWindow) {
      this.updateActiveNotificationData({ id, eventId, title });
    }

    AnalyticsLogger.notificationView({
      eventId,
      launchURL,
      notificationId: id,
      notificationType: type,
      subtitle,
      title,
      userId,
    });

    if (id && !isImpersonating) {
      this.markNotificationAsRead({ id, shouldSendAnalytics: false });
    }
  };

  // STORE HANDLERS ///////////////////////////////////////////////////////////
  _handleAuthStoreChange = async () => {
    const isLoggedIn = AuthService.isLoggedIn;

    const impersonating = AuthService.isImpersonating;
    const clientSettings = AuthService.getSession()?.clientSettings;
    const firstDayOfWeek = getDayOfWeekNumber(clientSettings?.firstDayOfWeek);

    const user = AuthStore.getUser();
    const { annotationTypes, authSettings, useLocalIpAsPrimary } = AuthStore.getCustomer();

    const {
      areNotificationsSet,
      authData: { loggedIn: isCurrentlyLoggedIn },
    } = this.state;

    const isImpersonating = impersonating;
    const isTestUser = AuthStore.isTestUser();
    const contextineCreds = AuthStore.getContextine();

    const newState = {
      authData: {
        authSettings,
        annotationTypes,
        contextineCreds,
        contextineEnabled: !!contextineCreds,
        firstDayOfWeek,
        isImpersonating,
        isTestUser,
        tenantId: get(user, 'app_metadata.tenantId', null),
        useLocalIpAsPrimary,
        user,
        expiration: get(user, 'exp') * 1000,
      },
      users: {
        impersonating,
      },
      appState: {},
    };

    if (isCurrentlyLoggedIn && !isLoggedIn) {
      // User was logged out
      newState.authData.loggedIn = false;
      newState.notifications = { data: [] };
    } else if (!isCurrentlyLoggedIn && isLoggedIn) {
      // User was logged in
      newState.authData.loggedIn = true;
    } else {
      newState.authData.loggedIn = isLoggedIn;
    }

    if (!areNotificationsSet && user) {
      newState.areNotificationsSet = true;
      // FETCH NOTIFICATIONS //
      clearTimeout(this.fetchNotificationsDebounce);
      this.fetchNotificationsDebounce = setTimeout(() => this.fetchNotifications(user));
    }

    this.cleanAndSetState(newState);
  };

  fetchNotifications = (user) => {
    if (AuthService.isGuest) {
      // Guests do not have access to Firebase
      return;
    }
    const { firestore } = this.props.store;
    const { firebaseToken } = AuthService.session;
    const { sub: doc } = user;
    this.notificationsQuery.doc = doc;

    // Always initialize archive cutoff so filterNotifications/isNotificationUnread never
    // pass `undefined` into a Firestore where() clause, even if sign-in or snapshot fails.
    this.notifArchiveCutoff = NOTIFICATIONS_MIN_QUERY_DATE;

    signInWithCustomToken(firebaseAuth, firebaseToken)
      .then(async () => {
        const snap = await firestore.get({ collection: 'users', doc });

        // The parent users/{sub} doc is provisioned by call-home-server and may not exist
        // for all users (e.g. brand-new accounts or project-id mismatches in dev).
        // A missing doc only affects saved read/archive cutoffs — the notifications
        // subcollection streams independently, so we always set the listener below.
        const data = snap?.data?.();
        if (data) {
          this.notifReadCutoff = data.metadata?.[NOTIFICATIONS_CUTOFF.read];
        } else {
          console.warn(`Firestore users/${doc} doc missing; using default notification cutoffs`);
        }

        // Update the default notifications query
        this.notificationsQuery.where.splice(0, 1, ['created', '>', this.notifArchiveCutoff]);

        // Set our default Firestore notifications listener
        const { docs } = await firestore.get(this.notificationsQuery);
        this.cleanAndSetState({ notifications: { inboxCount: this.getInboxCount({ docs }) } });
        firestore.setListener(this.notificationsQuery);
      })
      .catch((err) => toastUnexpectedError(err, { prefix: 'Error logging into firebase' }));
  };

  getInboxCount = ({ docs, filterBy = this.state.notifications.filterBy, notifications }) => {
    if (filterBy !== NOTIFICATIONS_FILTER_BY_STATE.all) {
      return this.state.notifications.inboxCount;
    }
    if (notifications) {
      return notifications.filter(this.isNotificationUnread).length;
    } else if (docs) {
      let count = 0;
      docs.forEach((ref) => {
        const data = ref.data();
        if (this.isNotificationUnread(data)) {
          count++;
        }
      });
      return count;
    }
  };

  isNotificationUnread = ({ created, state }) => {
    const readCutoff = this.notifReadCutoff || this.notifArchiveCutoff;
    const isNewerThanReadCutoff = created > readCutoff;
    return state === NOTIFICATIONS_FILTER_BY_STATE.unread && isNewerThanReadCutoff;
  };

  _handleCurrentActiveEventStoreChange = () => {
    const pinnedEvent = CurrentActiveEventStore.getPinned();
    const videoStartTime = CurrentActiveEventStore.getVideoStartTime();

    this.cleanAndSetState({
      events: {
        pinnedEvent,
        videoStartTime,
      },
    });
  };

  _handleEventFilterStoreChange = () => {
    const {
      locale,
      settings: {
        reports: { thresholdTemplateId, thresholdType },
      },
    } = this.state;

    const {
      eventFilterState,
      eventSubtypeFilter,
      eventTagsFilter,
      capsules,
      searchString,
      searchStringRegex,
      showRevenue,
    } = EventFilterStore.getAppState();

    const selectedFilter = EventFilterStore.getSelectedReport();
    const selectedFilterId = EventFilterStore.getSelectedReportId();
    const user = AuthStore.getUser();
    const userType = get(user, 'userType', null);
    const eventFilterReports = EventFilterStore.getReports();
    const reports = filter(
      eventFilterReports,
      (report) =>
        report && (!report.isDigestOnly() || (report.isDigestOnly() && userType !== 'standard')),
    );
    const eventFilterError = EventFilterStore.getError();
    const eventFilterSuccess = EventFilterStore.getSuccess();
    const message = EventFilterStore.getMessage();

    // SETTINGS: Notifications
    if (eventFilterError) {
      const errorMessage = !message ? (
        locale.notificationsPage.reportsChangesFailed
      ) : (
        <header className="custom-toast-message-body">
          <h1>{locale.notificationsPage.reportsChangesFailed}</h1>
          <p>{message}</p>
        </header>
      );
      toast.error(errorMessage, {
        autoClose: message ? MEDIUM_NOTIFICATION_DURATION : FAST_NOTIFICATION_DURATION,
      });
      EventFilterStore.resetReportNotificationToastState();
    } else if (eventFilterSuccess) {
      toast.success(message || locale.notificationsPage.reportsChangesSuccess, {
        autoClose: FAST_NOTIFICATION_DURATION,
      });
      EventFilterStore.resetReportNotificationToastState();
    }

    // Structure the data
    const newState = {
      events: {
        filters: {
          changeDate: new Date().getTime(),
          searchCapsules: capsules,
          searchString,
          searchStringRegex,
          selectedFilter,
          selectedFilterId,
          showRevenue,
          types: eventFilterState,
          subTypes: eventSubtypeFilter,
          tags: eventTagsFilter,
        },
      },
      filters: {
        eventFilters: eventFilterState,
      },
      reports: {
        /** Reports that are not digest only or is digest only but the user is not a standard user */
        data: reports,
        /** Reports that are both filter type and visible to the user */
        filterReports: EventFilterStore.getFilters(),
        updateTime: new Date().getTime(),
        fetched: EventFilterStore.getReportsFetched(),
      },
      settings: { reports: {} },
    };

    // SETTINGS: Reports
    if (thresholdTemplateId) {
      const canEnable = EventFilterStore.getRealTimeReportCanEnable({
        reportTemplateId: thresholdTemplateId,
        notificationType: thresholdType,
      });
      const average = EventFilterStore.getRealTimeReportAverage({
        reportTemplateId: thresholdTemplateId,
        notificationType: thresholdType,
      });
      newState.settings.reports.showThresholdAlert = canEnable === undefined ? false : !canEnable;
      newState.settings.reports.thresholdTemplateAverage = average || null;
      newState.settings.reports.thresholdTemplateId = null;
    } else if (eventFilterError) {
      newState.settings.reports.showThresholdAlert = false;
      newState.settings.reports.thresholdTemplateAverage = null;
      newState.settings.reports.thresholdTemplateId = null;
      newState.settings.reports.thresholdType = null;
    }

    // Only change state if the new state is different from the current state
    this.cleanAndSetState(newState);
  };

  _handleLocaleStoreChange = () => {
    const { localeCode } = this.props.localeState;
    const locale = LocaleUtil.getLocale(localeCode);
    const clientSettings = AuthService.getSession()?.clientSettings;
    const firstDayOfWeek = getDayOfWeekNumber(clientSettings?.firstDayOfWeek);

    // Update moment's global locale after locale has changed
    moment.locale(localeCode.toLowerCase());

    // Set firstDayOfWeek based on client settings
    if (firstDayOfWeek) {
      LocaleUtils.setFirstDayOfWeek(localeCode.toLowerCase(), firstDayOfWeek);
    }

    this.cleanAndSetState({
      locale,
      notifications: {
        filterByOptions: this.getFilterByOptions(locale),
      },
    });
  };

  _handleLocationStoreChange = () => {
    const { dispatch, store } = this.props;

    this.locationsById = LocationStore.getLocationsById();
    this.masterLocationsList = LocationStore.getLocations();
    this.masterRealLocationsList = LocationStore.getLocations(true);
    this.masterPhysicalLocationsList = LocationStore.getPhysicalLocations();

    const hasFetchedLocations = LocationStore.getHasFetchedLocations();

    const filterLocationIds = store.getState().filter?.locationIds;
    if (isEmpty(filterLocationIds)) {
      dispatch(setFilter({ locationIds: [ALL_LOCATIONS] }));
    }

    this.camerasDictionary = LocationStore.getAllLocationCamerasDict();
    const restrictions = LocationStore.getRestrictions();

    const newState = {
      locations: {
        loading: false,
        hasFetchedLocations,
        masterLocationsListLastUpdate: new Date().getTime(),
        restrictions,
      },
    };

    this.cleanAndSetState(newState);
  };

  _handlePreferenceStoreChange = () => {
    const shouldShowExperimentalFeatures = PreferenceStore.shouldShowExperimentalFeatures();
    const shouldShowAllEventThumbnails = PreferenceStore.shouldShowAllEventThumbnails();

    this.cleanAndSetState({
      settings: {
        preferences: {
          shouldShowExperimentalFeatures,
          shouldShowAllEventThumbnails,
        },
      },
    });
  };

  _handleUserStoreChange = () => {
    setTimeout(() => {
      const { smsId, smsNumber = '' } = UserStore.getRealTimeNotificationBindings();
      const users = UserStore.getUsers();

      const newState = {
        users: {
          current: UserStore.getCurrentUser(),
          data: users,
          digestSettings: UserStore.getDigestSettings(),
          hasSMSNumber: !!smsNumber,
          isLoaded: !isEmpty(users),
          loading: false,
          locationProcessing: false,
          smsId,
          smsNumber,
        },
        settings: {
          isDigestEnabled: UserStore.getIsDigestEnabled(),
          preferences: {
            shouldAllowSupportAccess: UserStore.getSupportAccessState(),
            supportAccessExpiresAt: UserStore.getSupportAccessExpiresAt(),
          },
        },
      };

      this.cleanAndSetState(newState);
    }, 0);
  };

  // STORE HANDLER HELPERS ////////////////////////////////////////////////////
  getFilterByOptions = (locale) => {
    const filterByMapping = (value, label) => ({ label: locale.notifications[label], value });
    return [
      {
        options: map(NOTIFICATIONS_FILTER_BY_STATE, filterByMapping),
      },
      {
        label: 'divider',
        options: map(NOTIFICATIONS_FILTER_BY_TYPE, filterByMapping),
      },
    ];
  };

  // Only sets state if the new state is different from the old state
  cleanAndSetState = (sourceState, callback) => {
    this.setState((state) => {
      const newState = {};
      let isStateChanged = false;

      // Remove any props that haven't changed
      forEach(sourceState, (sourceValue, prop) => {
        const isPropValueChange = !state[prop] || !isEqual(state[prop], sourceValue);

        if (isPropValueChange) {
          isStateChanged = true;

          if (isObject(sourceState[prop])) {
            // IMPORTANT: This constant is only defined inside this IF
            // statement to avoid unnecessary cloning operations. We use
            // cloneDeep to treat the store values as immutable.
            const clonedState = cloneDeep(sourceValue);
            // Make sure we do a deep merge of the two states,
            // to avoid conflicts.
            newState[prop] = mergeWith(
              {},
              state[prop],
              clonedState,
              this.mergeCustomizer({ prop }),
            );
          } else {
            newState[prop] = sourceValue;
          }
        }
      });

      // NOTE: this will still re-render in React 15, even if null
      return isStateChanged ? newState : null;
    }, callback);
  };

  mergeCustomizer =
    ({ prop, depth = 1 }) =>
    (originValue, newValue, key) => {
      const isOverridable =
        isEmpty(newValue) || isArray(newValue) || this.isKeyReplaceable(prop, key);
      if (!isOverridable && isPlainObject(originValue) && isPlainObject(newValue)) {
        if (depth < 3) {
          // If this is a non-replaceable object, deep merge it recursively
          return mergeWith(
            {},
            originValue,
            newValue,
            this.mergeCustomizer({ prop: `${prop}.${key}`, depth: depth + 1 }),
          );
        }
        return merge({}, originValue, newValue);
      }
      // Otherwise, overwrite the original (e.g., arrays)
      return newValue;
    };

  isKeyReplaceable(prop, key) {
    const keyPath = `${prop}.${key}`;
    return REPLACEABLE_STATE_PROPS.includes(keyPath);
  }

  // DATA CHANGE HANDLERS /////////////////////////////////////////////////////
  toggleNotifications = () =>
    this.cleanAndSetState({
      notifications: {
        showAllNotifications: !get(this, 'state.notifications.showAllNotifications'),
      },
    });

  resetReportSettings = () => {
    this.cleanAndSetState({
      settings: {
        reports: {
          thresholdTemplateAverage: null,
          thresholdType: null,
          showThresholdAlert: false,
        },
      },
    });
  };

  setReportSettings = (settings) => {
    const { thresholdTemplateId, thresholdType } = settings;
    this.cleanAndSetState({
      settings: {
        reports: {
          thresholdTemplateId,
          thresholdType,
        },
      },
    });
  };

  updateContextMenuStatus = ({ isOpen }) => {
    if (this.state.shared.isContextMenuOpen !== isOpen) {
      this.cleanAndSetState({ shared: { isContextMenuOpen: isOpen } });
    }
  };

  updateTimelineEvents = (events) => {
    this.cleanAndSetState({
      video: {
        timelineEvents: events || [],
      },
    });
  };

  syncTimelineEvent = (event) => {
    const {
      video: { timelineEvents },
    } = this.state;
    // Shallow clone timelineEvents.
    const updatedTimelineEvents = (timelineEvents || []).slice();

    const replaceIndex = updatedTimelineEvents.findIndex((e) => e.id === event.id);
    if (replaceIndex !== -1) {
      updatedTimelineEvents.splice(replaceIndex, 1, event);
    } else {
      const prevIndex = EventUtil.findClosestEventIndex(updatedTimelineEvents, event.startTime);
      const insertIndex = Math.min(prevIndex + 1, updatedTimelineEvents.length);
      updatedTimelineEvents.splice(insertIndex, 0, event);
    }

    this.updateTimelineEvents(updatedTimelineEvents);
  };

  updateLiveToggle = (value) => {
    if (FeaturesUtil.isLiveViewOnly()) {
      return;
    }
    StorageAPI.storeLocally(VIDEO_PAGE_LIVE_FLAG, value);
    this.cleanAndSetState({
      video: {
        rawIsLiveToggleOn: value,
      },
    });
  };

  updateHistogramOpenState = (status) => {
    this.cleanAndSetState({
      events: {
        histogramOpen: status,
      },
    });
  };
}

/**
 * Null-rendering Component that uses the notification hook from the class-based AuthenticatedApp
 */
const FcmBridge = ({ user, onNotification }) => {
  const isPushEligible = !isKiosk() && !new Browsers().isSafari;
  const callHomeUserId =
    isPushEligible && user?.type === 'callHome' ? user?.profile?.id : undefined;

  usePushNotificationLifecycle(callHomeUserId, user?.accessToken, onNotification);
  return null;
};

const ComposedAuthenticatedApp = (props) => {
  const location = useLocation();
  const [query] = useQueryParams();
  const outageStatus = useOutageStatus();
  const user = useUserSession();
  const impersonator = useImpersonator();
  const [doneSetup, setDoneSetup] = useState(false);
  const locale = useLocale();
  const db = useDb();

  const filterDate = useSelector((state) => state.filter.date);

  let guestTokenExpiryDate = undefined;
  if (user && user.type === 'guest') {
    guestTokenExpiryDate = new Date(user.jwtUser.exp * 1000);
  }

  useOnUserSessionChange((user, impersonator, prevUser) => {
    onUserSessionChange(db, props.dispatch, filterDate, user, prevUser, impersonator);
    setDoneSetup(true);
  });

  if (!doneSetup) {
    return <PageLoadingSpinner message={locale.general.sessionSetup} />;
  }

  return (
    <AuthenticatedApp
      {...props}
      user={user}
      impersonator={impersonator}
      router={{ location }}
      query={query}
      outageStatus={outageStatus}
      guestTokenExpiryDate={guestTokenExpiryDate}
      location={location}
    />
  );
};

export default connect((state) => {
  const { data, ordered } = state.firestore;
  return {
    localeState: state.localeState,
    notifications: ordered.notifications,
    notificationsDict: data.notifications,
    notificationsPreCutoff: ordered.notificationsPreCutoff,
    notificationsPreCutoffDict: data.notificationsPreCutoff,
  };
})(withStore(ComposedAuthenticatedApp));
